import { useCallback, useState } from 'react';
import { API_URL, POLL_INTERVAL_MS } from '../constants';
import { applyReportStyling } from '../utils/reportStyling';

export function useFileUpload() {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [status, setStatus] = useState('initial');
  const [textInput, setTextInput] = useState('');
  const [textUploaded, setTextUploaded] = useState(false);

  const [report, setReport] = useState(null);
  const [styledReport, setStyledReport] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);
  const [containsNDA, setContainsNDA] = useState(false);
  const [ndaReport, setNdaReport] = useState(null);

  const handleFileChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      const uploadedFile = e.target.files[0];
      setStatus('initial');
      setFile(uploadedFile);
      const fileExtension = uploadedFile.name.split('.').pop();
      setFileType(fileExtension);
      setTextUploaded(false);
    } else {
      setFile(null);
      setFileType('');
    }
  }, []);

  const handleTextChange = useCallback((text) => {
    setTextInput(text);
    if (textUploaded) {
      setTextUploaded(false);
    }
  }, [textUploaded]);

  const handleTextSubmit = useCallback(() => {
    if (textInput) {
      const blob = new Blob([textInput], { type: 'text/plain' });
      const fileName = `textinput_${Math.random().toString(36).substring(7)}`;
      const textFile = new File([blob], `${fileName}.txt`, { type: 'text/plain' });
      setFile(textFile);
      setFileType('txt');
      setStatus('initial');
      setTextUploaded(true);
    }
  }, [textInput]);

  const startPolling = useCallback((id) => {
    const intervalId = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/getReport?id=${id}`);
        const data = await response.json();

        if (data.status === 'completed') {
          clearInterval(intervalId);
          setStatus('success');

          let reportContent = data.report;
          if (data.report) {
            reportContent = reportContent.toString();
          } else {
            reportContent = 'No report available. Please try again.';
          }

          if (data.nda_template) {
            setNdaReport(data.nda_template);
          } else {
            setNdaReport(null);
          }

          setContainsNDA(data.contains_NDA);
          setReport(reportContent);
          setDisclaimer(data.disclaimer);
          setStyledReport(applyReportStyling(data.report));
          setFile(null);
          setTextUploaded(false);
          setTextInput('');
        }
      } catch (error) {
        console.error(error);
        clearInterval(intervalId);
        setStatus('fail');
        setReport('No report available. Please try again.');
        setStyledReport('No report available. Please try again.');
      }
    }, POLL_INTERVAL_MS);
  }, []);

  const handleUpload = useCallback(async (selectedState) => {
    if (!file) {
      return null;
    }

    setStatus('uploading');
    let state = 'NA';
    if (selectedState.value !== 'N/A') {
      state = selectedState.value;
    }
    const url = `${API_URL}/upload/${fileType}/${state}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
      });
      const data = await response.json();
      const presignedUrl = data.url;

      let contentType = `application/${fileType}`;

      if (['pdf', 'docx', 'doc', 'txt'].includes(fileType)) {
        if (fileType === 'txt') {
          contentType = 'text/plain';
        } else if (fileType === 'docx') {
          contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        } else if (fileType === 'doc') {
          contentType = 'application/msword';
        }
      }

      await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,
        },
      });

      setStatus('polling');
      const filename = data.id.split('.')[0];
      startPolling(filename);
    } catch (error) {
      console.error(error);
      setStatus('fail');
    }
  }, [file, fileType, startPolling]);

  const handleFileRemove = useCallback(() => {
    setFile(null);
    setFileType('');
    setTextUploaded(false);
    setStatus('initial');
  }, []);

  const reset = useCallback(() => {
    setFile(null);
    setStatus('initial');
    setFileType('');
    setTextInput('');
    setTextUploaded(false);
    setReport(null);
    setStyledReport(null);
    setDisclaimer(null);
    setContainsNDA(false);
    setNdaReport(null);
  }, []);

  return {
    file,
    fileType,
    status,
    textInput,
    textUploaded,
    report,
    styledReport,
    disclaimer,
    containsNDA,
    ndaReport,
    handleFileChange,
    handleFileRemove,
    handleTextChange,
    handleTextSubmit,
    handleUpload,
    reset,
  };
}
